/** Price Reductions feed (v1 price-reductions.js priceReductionsPanel). A
 * read-only, newest-first roll-up of every price cut across all leads — from the
 * per-lead reduction log (priceDrops) or a single current priceDrop when the log
 * isn't present. No writes, no backend; it just reshapes the leads already in
 * memory. */
import type { Lead } from "../../types";
import { moneyExact } from "../../lib/money";

export interface DropRow {
  address: string;
  county: string;
  source: string;
  date: string | null;
  from: number | null;
  to: number | null;
  drop: number; // absolute dollars
  pct: number | null;
  preCrm: boolean;
}

const abs = (n: number) => (n < 0 ? -n : n);

export function buildDropFeed(leads: Lead[]): DropRow[] {
  const feed: DropRow[] = [];
  for (const p of leads) {
    const drops = Array.isArray(p.priceDrops) ? p.priceDrops : [];
    if (drops.length) {
      for (const e of drops) {
        const from = e.from, to = e.to;
        feed.push({
          address: p.address, county: p.county ?? "", source: p.source ?? "",
          date: e.date, from, to,
          drop: abs((from ?? 0) - (to ?? 0)), pct: e.pct, preCrm: e.preCrm,
        });
      }
    } else if (p.priceDrop && p.priceDrop.to != null) {
      const from = p.priceDrop.from, to = p.priceDrop.to;
      feed.push({
        address: p.address, county: p.county ?? "", source: p.source ?? "",
        date: null, from, to,
        drop: abs((from ?? 0) - (to ?? 0)), pct: p.priceDrop.pct, preCrm: false,
      });
    }
  }
  // newest first; undated rows sort last (v1 shows the log in date order)
  const t = (d: string | null): number => {
    if (!d) return -Infinity;
    const n = Date.parse(d);
    return Number.isNaN(n) ? -Infinity : n;
  };
  feed.sort((a, b) => t(b.date) - t(a.date));
  return feed;
}

export function fmtDropDate(d: string | null): string {
  if (!d) return "—";
  const n = Date.parse(d);
  if (Number.isNaN(n)) return d;
  return new Date(n).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export const fmtUsd = (n: number | null): string => (n == null ? "—" : moneyExact(n));
